import {ApolloError} from '@apollo/client';
import {Alert, Button, createStyles, Group, Modal, Text} from '@mantine/core';
import React, {useState} from 'react';

import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import ProteinCreationComponentSelectionPane from 'antibodies/nested-data-creation/full-screen/ProteinCreationComponentSelectionPane';
import ProteinCreationSearchPane from 'antibodies/nested-data-creation/full-screen/ProteinCreationSearchPane';
import {NdcSpreadsheetComponentFormatType} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {pluralize} from 'BenchlingUtil';
import {BioentityInfo} from 'folder-items/FileConstants';
import {getApolloClient} from 'graphql-client/ApolloClient';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {
  ProteinNestedComponentStructuredTableColumn,
  StructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import notifications from 'notifications';
import {
  InaccessibleBioentitiesQuery,
  InaccessibleBioentitiesQueryVariables,
} from 'samples/grids/__generated/InaccessibleBioentitiesQuery';
import {INACCESSIBLE_BIOENTITIES_QUERY} from 'samples/grids/GridQueryUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ModalFooter from 'ui-library/modals/ModalFooter';

export const ADD_FROM_SEARCH_TITLE = 'Add from search';
export const LOAD_ENTITIES_ERROR = "The selected entities couldn't be loaded. Try again.";
export const NO_ENTITIES_AVAILABLE_ERROR =
  'None of the selected entities are available to you. Select other entities and try again.';

export function getUnavailableEntitiesWarning(count: number): string {
  return `${count} of the selected entities ${count === 1 ? 'is' : 'are'} no longer available to you.`;
}

const SEARCH_STEP_Y_OFFSET = '2.5vh';

const useStyles = createStyles({
  // The search step fills the viewport, and the search pane takes the body's remaining height so that only
  // the results scroll, not the modal.
  searchContent: {
    display: 'flex',
    flexDirection: 'column',
    height: `calc(100dvh - 2 * ${SEARCH_STEP_Y_OFFSET})`,
    overflowY: 'hidden',
  },
  searchBody: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
});

enum Step {
  SELECT_COMPONENT = 'SELECT_COMPONENT',
  SEARCH = 'SEARCH',
}

/**
 * Load the entities in selection order, leaving out any the user can't access. The search results usually put the
 * entities in the Apollo cache already, so this rarely needs the network.
 */
async function fetchAccessibleEntities(ids: Array<string>): Promise<Array<BioentityInfo>> {
  const {data} = await getApolloClient().query<
    InaccessibleBioentitiesQuery,
    InaccessibleBioentitiesQueryVariables
  >({query: INACCESSIBLE_BIOENTITIES_QUERY, variables: {ids}});
  const namesById = new Map(accessibleObjs(data.bioentities).map(({id, name}) => [id, name]));
  return ids.flatMap((id) => {
    const name = namesById.get(id);
    return name == null ? [] : [{id, name}];
  });
}

interface ProteinCreationSearchModalProps {
  format: AntibodyFormat;
  startingType: NdcSpreadsheetComponentFormatType;
  columns: ReadonlyArray<StructuredTableColumn>;
  /** Called with the chosen column and the entities selected in search, in selection order. */
  onAddEntities: (columnCid: string, entities: Array<BioentityInfo>) => void;
  onClose: () => void;
}

/**
 * Adds existing entities to a component column of the protein creation table: the user picks the column, then
 * searches for the entities.
 */
export default function ProteinCreationSearchModal({
  format,
  startingType,
  columns,
  onAddEntities,
  onClose,
}: ProteinCreationSearchModalProps): JSX.Element {
  const {classes} = useStyles();
  const [step, setStep] = useState(Step.SELECT_COMPONENT);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedColumn =
    columns.find(
      (column): column is ProteinNestedComponentStructuredTableColumn =>
        column.columnType === TableColumnType.PROTEIN_NESTED_COMPONENT && column.name === selectedLabel
    ) ?? null;

  const isSearchStep = step === Step.SEARCH && selectedColumn != null;

  const selectedCount = selectedIds.length;

  // The modal can't be closed and the selection can't change while the selected entities load, so the loaded
  // entities always match the selection and there's a modal to add them from.
  const handleClose = (): void => {
    if (!isAdding) {
      onClose();
    }
  };

  const handleSelectIds = (ids: Array<string>): void => {
    if (!isAdding) {
      setSelectedIds(ids);
    }
  };

  const handleAdd = async (column: ProteinNestedComponentStructuredTableColumn): Promise<void> => {
    setIsAdding(true);
    setError(null);
    let entities: Array<BioentityInfo>;
    try {
      entities = await fetchAccessibleEntities(selectedIds);
    } catch (err: unknown) {
      if (!(err instanceof ApolloError)) {
        throw err;
      }
      setError(LOAD_ENTITIES_ERROR);
      return;
    } finally {
      // Before adding, because adding the entities closes the modal.
      setIsAdding(false);
    }
    if (entities.length === 0) {
      setSelectedIds([]);
      setError(NO_ENTITIES_AVAILABLE_ERROR);
      return;
    }
    const unavailableCount = selectedIds.length - entities.length;
    if (unavailableCount > 0) {
      notifications.warn(getUnavailableEntitiesWarning(unavailableCount));
    }
    onAddEntities(column.cid, entities);
  };

  return (
    <Modal
      title={ADD_FROM_SEARCH_TITLE}
      opened={true}
      onClose={handleClose}
      size={isSearchStep ? 'min(1600px, 95vw)' : 'xl'}
      yOffset={isSearchStep ? SEARCH_STEP_Y_OFFSET : undefined}
      classNames={isSearchStep ? {content: classes.searchContent, body: classes.searchBody} : undefined}
      closeButtonProps={{'aria-label': 'Close', disabled: isAdding}}
      closeOnClickOutside={!isAdding}
      closeOnEscape={!isAdding}
      lockScroll={false}
    >
      {isSearchStep ? (
        <ProteinCreationSearchPane
          column={selectedColumn}
          selectedIds={selectedIds}
          onSelectIds={handleSelectIds}
        />
      ) : (
        <ProteinCreationComponentSelectionPane
          format={format}
          startingType={startingType}
          selectedLabel={selectedLabel}
          onSelectLabel={setSelectedLabel}
        />
      )}
      {error != null && (
        <Alert icon={<BenchlingIcon icon="exclamation-circle" />} mt="md" variant="error">
          {error}
        </Alert>
      )}
      <ModalFooter>
        <Group position="apart">
          <Button variant="secondary" disabled={isAdding} onClick={handleClose}>
            Cancel
          </Button>
          {isSearchStep ? (
            <Group spacing="sm">
              <Text color="dimmed" size="sm">
                {selectedCount > 0
                  ? `${selectedCount} ${pluralize(selectedCount, 'sequence')} will be added`
                  : 'Nothing selected yet'}
              </Text>
              <Button
                variant="secondary"
                disabled={isAdding}
                onClick={() => {
                  setStep(Step.SELECT_COMPONENT);
                  setSelectedIds([]);
                  setError(null);
                }}
              >
                Back
              </Button>
              <Button
                disabled={selectedCount === 0}
                loading={isAdding}
                onClick={() => {
                  handleAdd(selectedColumn);
                }}
              >
                {selectedCount > 0 ? `Add ${selectedCount}` : 'Add'}
              </Button>
            </Group>
          ) : (
            <Button
              disabled={selectedColumn == null}
              onClick={() => {
                setStep(Step.SEARCH);
              }}
            >
              Next
            </Button>
          )}
        </Group>
      </ModalFooter>
    </Modal>
  );
}
